import * as THREE from 'three'

const ROOT_TRANSLATION_TARGET=/(?:^|[.\[\]\\/])(root|hips|pelvis|armature)(?:$|[.\[\]\\/])/i
export type RigRootMotionMode='planar-in-place'|'full-in-place'
export type RigRootMotionProfile={
 mode:RigRootMotionMode
 skeletonDiagonal:number
 maxRootTranslationDiagonal:number
 ratio:number
 rootTrackCount:number
}
export type RigTranslationAnchors=ReadonlyMap<string,THREE.Vector3>
export type RigScaleAnchors=ReadonlyMap<string,THREE.Vector3>

function translationTargetName(trackName:string){
 const bracket=trackName.match(/\[([^\]]+)\]\.position$/i)?.[1]
 if(bracket)return bracket.toLowerCase()
 const direct=trackName.match(/(?:^|[.\\/])([^.[\\/]+)\.position$/i)?.[1]
 return direct?.toLowerCase()??null
}

export function synchronizeFighterBindPose(root:THREE.Object3D){
 const visited=new Set<THREE.Skeleton>()
 root.traverse(node=>{if(node instanceof THREE.SkinnedMesh&&!visited.has(node.skeleton)){visited.add(node.skeleton);node.skeleton.pose()}})
 root.updateMatrixWorld(true)
 return visited.size
}
export function captureRigTranslationAnchors(root:THREE.Object3D):RigTranslationAnchors{
 const anchors=new Map<string,THREE.Vector3>()
 const skeletons=new Set<THREE.Skeleton>(),snapshots=new Map<THREE.Bone,{position:THREE.Vector3;quaternion:THREE.Quaternion;scale:THREE.Vector3}>()
 root.traverse(node=>{
  if(node instanceof THREE.SkinnedMesh)skeletons.add(node.skeleton)
  if(node instanceof THREE.Bone&&!snapshots.has(node))snapshots.set(node,{position:node.position.clone(),quaternion:node.quaternion.clone(),scale:node.scale.clone()})
 })
 // Imported fighter clips may encode bone translations in authoring units (cm) while
 // inverse bind matrices describe the actual runtime bind pose (m). Skeleton.pose()
 // is the authoritative source for local translation anchors.
 for(const skeleton of skeletons)skeleton.pose()
 root.updateMatrixWorld(true)
 root.traverse(node=>{if(node.name)anchors.set(node.name.toLowerCase(),node.position.clone())})
 for(const [bone,snapshot] of snapshots){bone.position.copy(snapshot.position);bone.quaternion.copy(snapshot.quaternion);bone.scale.copy(snapshot.scale)}
 root.updateMatrixWorld(true)
 return anchors
}
export function captureRigScaleAnchors(root:THREE.Object3D):RigScaleAnchors{
 const anchors=new Map<string,THREE.Vector3>()
 const skeletons=new Set<THREE.Skeleton>(),snapshots=new Map<THREE.Bone,{position:THREE.Vector3;quaternion:THREE.Quaternion;scale:THREE.Vector3}>()
 root.traverse(node=>{
  if(node instanceof THREE.SkinnedMesh)skeletons.add(node.skeleton)
  if(node instanceof THREE.Bone&&!snapshots.has(node))snapshots.set(node,{position:node.position.clone(),quaternion:node.quaternion.clone(),scale:node.scale.clone()})
 })
 for(const skeleton of skeletons)skeleton.pose()
 root.updateMatrixWorld(true)
 root.traverse(node=>{if(node.name)anchors.set(node.name.toLowerCase(),node.scale.clone())})
 for(const [bone,snapshot] of snapshots){bone.position.copy(snapshot.position);bone.quaternion.copy(snapshot.quaternion);bone.scale.copy(snapshot.scale)}
 root.updateMatrixWorld(true)
 return anchors
}
function scaleTargetName(trackName:string){
 const bracket=trackName.match(/\[([^\]]+)\]\.scale$/i)?.[1]
 if(bracket)return bracket.toLowerCase()
 const direct=trackName.match(/(?:^|[.\\/])([^.[\\/]+)\.scale$/i)?.[1]
 return direct?.toLowerCase()??null
}

export function isPlanarRootTranslationTrack(name:string){
 return name.toLowerCase().endsWith('.position')&&ROOT_TRANSLATION_TARGET.test(name)
}

export function countPlanarRootTranslationTracks(clips:readonly THREE.AnimationClip[]){
 return clips.reduce((count,clip)=>count+clip.tracks.filter(track=>isPlanarRootTranslationTrack(track.name)&&track.getValueSize()>=3).length,0)
}

export function measureSkeletonDiagonal(root:THREE.Object3D){
 root.updateMatrixWorld(true)
 const box=new THREE.Box3(),point=new THREE.Vector3()
 let bones=0
 root.traverse(node=>{
  if(!(node as THREE.Bone).isBone)return
  node.getWorldPosition(point)
  if(Number.isFinite(point.x)&&Number.isFinite(point.y)&&Number.isFinite(point.z)){box.expandByPoint(point);bones++}
 })
 if(bones<2||box.isEmpty())return 0
 return box.getSize(point).length()
}

export function measureMaxRootTranslationDiagonal(clips:readonly THREE.AnimationClip[]){
 let maxDiagonal=0,rootTrackCount=0
 for(const clip of clips)for(const track of clip.tracks){
  if(!isPlanarRootTranslationTrack(track.name)||track.getValueSize()<3)continue
  const size=track.getValueSize(),values=track.values
  let minX=Infinity,minY=Infinity,minZ=Infinity,maxX=-Infinity,maxY=-Infinity,maxZ=-Infinity,samples=0
  for(let offset=0;offset+2<values.length;offset+=size){
   const x=Number(values[offset]),y=Number(values[offset+1]),z=Number(values[offset+2])
   if(!Number.isFinite(x)||!Number.isFinite(y)||!Number.isFinite(z))continue
   minX=Math.min(minX,x);minY=Math.min(minY,y);minZ=Math.min(minZ,z)
   maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);maxZ=Math.max(maxZ,z);samples++
  }
  if(samples){rootTrackCount++;maxDiagonal=Math.max(maxDiagonal,Math.hypot(maxX-minX,maxY-minY,maxZ-minZ))}
 }
 return{maxDiagonal,rootTrackCount}
}

/**
 * Fight simulation owns fighter displacement: Combatant + HeroChoreography own X/Z,
 * while aerialMotion owns Y. Controller-driven animation therefore keeps root/hips/
 * pelvis translation fully in-place on every axis. Bone rotations and non-root local
 * motion remain authored; only character-level translation is removed so an imported
 * clip can never lift or slide the whole skinned fighter away from the arena.
 *
 * Root-motion profiling is retained for diagnostics and compatibility metadata.
 */
export function deriveRigRootMotionProfile(root:THREE.Object3D,clips:readonly THREE.AnimationClip[],fullInPlaceThreshold=2):RigRootMotionProfile{
 const skeletonDiagonal=measureSkeletonDiagonal(root)
 const {maxDiagonal,rootTrackCount}=measureMaxRootTranslationDiagonal(clips)
 const ratio=skeletonDiagonal>1e-6?maxDiagonal/skeletonDiagonal:0
 return{mode:ratio>fullInPlaceThreshold?'full-in-place':'planar-in-place',skeletonDiagonal,maxRootTranslationDiagonal:maxDiagonal,ratio,rootTrackCount}
}

export function stabilizeScaleTrack(track:THREE.KeyframeTrack,anisotropyLimit=1.035){
 if(!track.name.toLowerCase().endsWith('.scale')||track.getValueSize()!==3)return 0
 const values=track.values,size=3
 let repaired=0
 for(let offset=0;offset+2<values.length;offset+=size){
  const x=Number(values[offset]),y=Number(values[offset+1]),z=Number(values[offset+2])
  const valid=[x,y,z].every(v=>Number.isFinite(v)&&v>1e-6)
  const min=valid?Math.min(x,y,z):0,max=valid?Math.max(x,y,z):Infinity
  if(valid&&max/min<=anisotropyLimit)continue
  const sorted=valid?[x,y,z].sort((a,b)=>a-b):[1,1,1],uniform=THREE.MathUtils.clamp(sorted[1],.72,1.28)
  values[offset]=uniform;values[offset+1]=uniform;values[offset+2]=uniform;repaired++
 }
 return repaired
}

export function makeControllerDrivenClips(clips:readonly THREE.AnimationClip[],mode:RigRootMotionMode='planar-in-place',anchors?:RigTranslationAnchors,scaleAnchors?:RigScaleAnchors){
 return clips.map(source=>{
  const clip=source.clone()
  for(const track of clip.tracks){
   stabilizeScaleTrack(track)
   if(track.name.toLowerCase().endsWith('.scale')&&track.getValueSize()===3){
    const target=scaleTargetName(track.name),anchor=target?scaleAnchors?.get(target):undefined
    if(anchor){
     for(let offset=0;offset+2<track.values.length;offset+=3){track.values[offset]=anchor.x;track.values[offset+1]=anchor.y;track.values[offset+2]=anchor.z}
    }
   }
   if(!isPlanarRootTranslationTrack(track.name)||track.getValueSize()<3)continue
   const values=track.values,size=track.getValueSize()
   const target=translationTargetName(track.name),anchor=target?anchors?.get(target):undefined
   const firstX=Number(values[0]),firstY=Number(values[1]),firstZ=Number(values[2])
   const baseX=anchor?.x??firstX,baseY=anchor?.y??firstY,baseZ=anchor?.z??firstZ
   for(let offset=0;offset+2<values.length;offset+=size){
    const authoredY=Number(values[offset+1])
    values[offset]=baseX
    values[offset+2]=baseZ
    values[offset+1]=mode==='full-in-place'
      ? baseY
      : anchor
        ? baseY+(Number.isFinite(authoredY)&&Number.isFinite(firstY)?authoredY-firstY:0)
        : authoredY
   }
  }
  return clip
 })
}

export function makeControllerDrivenPlanarClips(clips:readonly THREE.AnimationClip[],anchors?:RigTranslationAnchors,scaleAnchors?:RigScaleAnchors){
 return makeControllerDrivenClips(clips,'planar-in-place',anchors,scaleAnchors)
}

export type RigContactSlot='foot_l'|'foot_r'|'toe_l'|'toe_r'|'hand_l'|'hand_r'
export type RigContactAnchors=Record<RigContactSlot,string|null>

const CONTACT_ALIASES:Record<RigContactSlot,RegExp[]>={
 foot_l:[/^leftfoot$/i,/left.*foot/i,/foot.*(?:[._ -]l|left)/i],
 foot_r:[/^rightfoot$/i,/right.*foot/i,/foot.*(?:[._ -]r|right)/i],
 toe_l:[/^lefttoebase$/i,/left.*toe/i,/toe.*(?:[._ -]l|left)/i],
 toe_r:[/^righttoebase$/i,/right.*toe/i,/toe.*(?:[._ -]r|right)/i],
 hand_l:[/^lefthand$/i,/left.*hand/i,/hand.*(?:[._ -]l|left)/i],
 hand_r:[/^righthand$/i,/right.*hand/i,/hand.*(?:[._ -]r|right)/i],
}

export function resolveRigContactAnchors(root:THREE.Object3D):RigContactAnchors{
 const names:string[]=[]
 root.traverse(node=>{if((node as THREE.Bone).isBone&&node.name)names.push(node.name)})
 const out={} as RigContactAnchors
 for(const [slot,patterns] of Object.entries(CONTACT_ALIASES) as [RigContactSlot,RegExp[]][])
  out[slot]=names.find(name=>patterns.some(pattern=>pattern.test(name)))??null
 return out
}

export function rigContactAnchorCount(anchors:RigContactAnchors){
 return Object.values(anchors).filter(Boolean).length
}

export function rigContactProfileComplete(anchors:RigContactAnchors){
 return rigContactAnchorCount(anchors)===Object.keys(CONTACT_ALIASES).length
}
